import { clearlyInsideBoundary, clearlyOutsideBoundary, insideBoundary } from "./journey";
import type { Coordinates, PickupStatus } from "../shared/types";

export const PICKUP_ZONES = [
	{ id: "north-hyde", name: "North - Hyde St.", color: "#8aaff0", boundary: [{ lat: 37.7793645309, lon: -122.4150680373 }, { lat: 37.7793814691, lon: -122.4149339627 }, { lat: 37.7789784691, lon: -122.4148524627 }, { lat: 37.7789615309, lon: -122.4149865373 }] },
	{ id: "south-8th", name: "South - 8th St.", color: "#8aaff0", boundary: [{ lat: 37.7785682201, lon: -122.4145722760 }, { lat: 37.7786767799, lon: -122.4144337240 }, { lat: 37.7784407801, lon: -122.4141377240 }, { lat: 37.7783322199, lon: -122.4142762760 }] },
	{ id: "library-loading", name: "Library loading", color: "#8aaff0", boundary: [{ lat: 37.7789059850, lon: -122.4155407020 }, { lat: 37.7789604850, lon: -122.4151227020 }, { lat: 37.7788675150, lon: -122.4151032980 }, { lat: 37.7788130150, lon: -122.4155212980 }] },
	{ id: "library-loading-ii", name: "Library loading II", color: "#8aaff0", boundary: [{ lat: 37.7788209820, lon: -122.4162631727 }, { lat: 37.7788654820, lon: -122.4159061727 }, { lat: 37.7787635180, lon: -122.4158858273 }, { lat: 37.7787190180, lon: -122.4162428273 }] },
] as const;

export const GLEN_PICKUP_ZONES = [
	{ id: "bosworth", name: "Bosworth Street", color: "#8aaff0", boundary: [{ lat: 37.733435381866, lon: -122.433979768365 }, { lat: 37.733336381906, lon: -122.433509768365 }, { lat: 37.733275618048, lon: -122.433530231635 }, { lat: 37.733374618089, lon: -122.434000231635 }] },
] as const;

export type PickupZoneId = (typeof PICKUP_ZONES)[number]["id"] | (typeof GLEN_PICKUP_ZONES)[number]["id"];

export function pickupZonesFor(stationId: string | null) {
	return stationId === "CIVC" ? PICKUP_ZONES : stationId === "GLEN" ? GLEN_PICKUP_ZONES : [];
}
export interface MapFix extends Coordinates {
	fixAtMs: number;
	accuracyMeters: number;
	speedMetersPerSecond: number | null;
	source?: "manual-test";
}
export interface PickupMapState {
	pickupId: string;
	driver: { name: string; position: MapFix | null };
	rider: { name: string; position: MapFix | null };
	proposedZoneId: PickupZoneId | null;
	proposedAtMs: number | null;
	currentZoneId: PickupZoneId | null;
	spotChangedAtMs: number | null;
	spotChangedBy: "driver" | "rider" | null;
	updatedAtMs: number;
}

export const PICKUP_MAP_AREA: readonly Coordinates[] = [
	{ lat: 37.77975, lon: -122.41675 }, { lat: 37.77975, lon: -122.41375 },
	{ lat: 37.77800, lon: -122.41375 }, { lat: 37.77800, lon: -122.41675 },
];

export function pickupZoneId(value: unknown): PickupZoneId | null {
	return [...PICKUP_ZONES, ...GLEN_PICKUP_ZONES].find(zone => zone.id === value)?.id ?? null;
}

/** The agreed kerb as it is written on both screens. Null for anything this build does not know. */
export function pickupZoneName(value: unknown): string | null {
	return [...PICKUP_ZONES, ...GLEN_PICKUP_ZONES].find(zone => zone.id === value)?.name ?? null;
}

export function pickupZoneStation(zoneId: PickupZoneId): "CIVC" | "GLEN" {
	return zoneId === "bosworth" ? "GLEN" : "CIVC";
}

export function pickupMapAllowed(status: PickupStatus, sharing: boolean, isParticipant: boolean): boolean {
	return status === "active" && sharing && isParticipant;
}

export const LAST_KNOWN_FIX_MS = 7 * 24 * 60 * 60_000;

export function mapFix(value: unknown, nowMs: number, maxAgeMs = 30 * 60_000): MapFix | null {
	if (value == null || typeof value !== "object") return null;
	const raw = value as Record<string, unknown>;
	const finite = (input: unknown): input is number => typeof input === "number" && Number.isFinite(input);
	if (!finite(raw.lat) || Math.abs(raw.lat) > 90 || !finite(raw.lon) || Math.abs(raw.lon) > 180 ||
		!finite(raw.accuracyMeters) || raw.accuracyMeters < 0 || !finite(raw.fixAtMs) || raw.fixAtMs <= 0 ||
		raw.fixAtMs > nowMs + 30_000 || nowMs - raw.fixAtMs > maxAgeMs) return null;
	return {
		lat: raw.lat, lon: raw.lon, accuracyMeters: raw.accuracyMeters, fixAtMs: raw.fixAtMs,
		speedMetersPerSecond: finite(raw.speedMetersPerSecond) && raw.speedMetersPerSecond >= 0 ? raw.speedMetersPerSecond : null,
		...(raw.source === "manual-test" ? { source: "manual-test" as const } : {}),
	};
}

export function mapFixAge(position: MapFix | null, nowMs: number, maxAgeMs = 30 * 60_000): "missing" | "recent" | "stale" {
	if (!position || !mapFix(position, nowMs, maxAgeMs)) return "missing";
	return nowMs - position.fixAtMs > 2 * 60_000 ? "stale" : "recent";
}

export function planningMapFix(local: unknown, stored: unknown, nowMs: number): MapFix | null {
	const current = mapFix(local, nowMs, LAST_KNOWN_FIX_MS);
	const saved = mapFix(stored, nowMs, LAST_KNOWN_FIX_MS);
	return current && (!saved || current.fixAtMs >= saved.fixAtMs) ? current : saved;
}

export function pickupZoneEvidence(position: MapFix | null, zoneId: PickupZoneId, nowMs: number): "inside" | "uncertain" | "outside" | "unknown" {
	if (mapFixAge(position, nowMs) !== "recent" || !position) return "unknown";
	const zone = [...PICKUP_ZONES, ...GLEN_PICKUP_ZONES].find(candidate => candidate.id === zoneId)!;
	if (clearlyInsideBoundary(position, zone.boundary)) return "inside";
	if (clearlyOutsideBoundary(position, zone.boundary)) return "outside";
	return "uncertain";
}

export function inPickupMapArea(position: MapFix | null, nowMs: number): boolean {
	return mapFixAge(position, nowMs) !== "missing" && insideBoundary(position, PICKUP_MAP_AREA);
}
/** Screen-pixel travel of the two-finger midpoint that counts as a pan rather than a pinch's natural drift. */
export const TWO_FINGER_PAN_PX = 32;

export interface ScreenPoint { x: number; y: number }

export function touchMidpoint(points: readonly ScreenPoint[]): ScreenPoint | null {
	if (points.length < 2) return null;
	return { x: (points[0].x + points[1].x) / 2, y: (points[0].y + points[1].y) / 2 };
}

/** A two-finger gesture whose midpoint travels is a pan; one that only spreads or pinches is a zoom. */
export function isTwoFingerPan(start: ScreenPoint | null, current: ScreenPoint | null, thresholdPx = TWO_FINGER_PAN_PX): boolean {
	if (!start || !current) return false;
	return Math.hypot(current.x - start.x, current.y - start.y) >= thresholdPx;
}
